import { defineStore } from 'pinia';
import { ProductMapper } from '@/utils/mappers/ProductMapper';
import { EcommerceProduct, EcommerceProductApi, Option, Product, ProductApi, VariantEcom } from '@/types/Product';
import { CanvasTexture } from 'three';
import { GraduationMapper } from '@/utils/mappers/GraduationMapper';
import { Color, Material, HexColor } from '@/types/Material';
import { EcommerceProductMapper } from '@/utils/mappers/EcommerceProductMapper';
import { PriceDetail } from '@/types/Price';

export const useProductStore = defineStore('product', {
    state: () => ({
        product: {} as Product,
        canvasTexture: null as CanvasTexture | null,
        _prices: null as PriceDetail | null,
        selectedMaterial: null as Material | null,
        selectedColor: null as Color | HexColor | null,
        selectedMaterialIndices: [] as number[],
        graduations: [] as Graduation[],
        screenshots: [] as string[],
        screenshotZones: [] as Array<{ label: string; screenshots: string[] }>,
        ecommerceProduct: {
            options: [],
            variants: []
        } as EcommerceProduct,
        selectedOptions: {} as Record<string, string>
    }),
    actions: {
        setProduct(productData: ProductApi) {
            let chosenColorName: string | undefined;
            const ecommerceColorOption = this.ecommerceProduct.options.find(
                (option: Option) => option.type === 'Color'
            );
            if (ecommerceColorOption) {
                chosenColorName = this.selectedOptions[ecommerceColorOption.name];
            }
            const mappedProduct = ProductMapper.getProductFromData(productData, chosenColorName);
            this.product = mappedProduct;
            console.log('Product after mapping:', mappedProduct);
            this.selectedColor = this.product.material.colorsFor3DObject[0];
            this.applyEcommerceColorSelectionToMaterials();
        },
        setCanvasTexture(newCanvasTexture: CanvasTexture) {
            this.canvasTexture = newCanvasTexture;
        },
        setGraduations(graduations: GraduationApi[]) {
            const graduationList: Graduation[] = [];
            graduations.forEach((graduation: GraduationApi) => {
                graduationList.push(GraduationMapper.getGraduationFromData(graduation));
            });

            this.graduations = graduationList || [];
        },
        setScreenshots(screenshots: string[]) {
            this.screenshots = screenshots;
        },
        setEcommerceProduct(ecommerceProductApi: EcommerceProductApi) {
            if (!ecommerceProductApi) {
                return;
            }
            const ecommerceProduct = EcommerceProductMapper.getEcommerceProductFromData(ecommerceProductApi);
            this.ecommerceProduct = ecommerceProduct;
        },
        updateSelectedOptions(options: Record<string, string>) {
            if (!this.ecommerceProduct.options || !this.ecommerceProduct.variants) {
                console.error('Missing options or variants in ecommerce product');
                return;
            }
            this.selectedOptions = { ...this.selectedOptions, ...options };

            if (this.ecommerceColorOption) {
                const selectedColorName = this.selectedOptions[this.ecommerceColorOption.name];
                const selectedColor = this.colors.find((color) => color.name === selectedColorName);
                if (selectedColor) {
                    this.selectedColor = ProductMapper.convertColorToHexa(selectedColor);
                }
            }
            this.applyEcommerceColorSelectionToMaterials();
        },
        applyEcommerceColorSelectionToMaterials() {
            const ecommerceColorOption = this.ecommerceProduct.options.find(
                (option: Option) => option.type === 'Color'
            );
            if (!ecommerceColorOption) {
                return;
            }

            const selectedColorName = this.selectedOptions[ecommerceColorOption.name];
            if (!selectedColorName || !Array.isArray(this.product.meshes) || this.product.meshes.length === 0) {
                return;
            }

            const nextIndices =
                this.selectedMaterialIndices.length === this.product.meshes.length
                    ? [...this.selectedMaterialIndices]
                    : Array.from({ length: this.product.meshes.length }, () => 0);

            let hasChanged = false;
            this.product.meshes.forEach((mesh, meshIndex) => {
                const materialIndex =
                    mesh.materials?.findIndex((material) => material.colorName === selectedColorName) ?? -1;
                if (materialIndex >= 0 && nextIndices[meshIndex] !== materialIndex) {
                    nextIndices[meshIndex] = materialIndex;
                    hasChanged = true;
                }
            });

            if (hasChanged || this.selectedMaterialIndices.length !== nextIndices.length) {
                this.selectedMaterialIndices = nextIndices;
            }
        },
        setSelectedColor(color: Color) {
            if (!color) {
                return;
            }
            const ecommerceColorOption = this.ecommerceProduct.options.find(
                (option: Option) => option.type === 'Color'
            );
            if (ecommerceColorOption) {
                this.selectedOptions[ecommerceColorOption.name] = color.name as string;
            }
            this.selectedColor = ProductMapper.convertColorToHexa(color);
        },
        initSelectedMaterialIndices(meshCount: number) {
            this.selectedMaterialIndices = Array.from({ length: meshCount }, () => 0);
            this.applyEcommerceColorSelectionToMaterials();
        },
        setSelectedMaterialIndex(meshIndex: number, materialIndex: number) {
            if (meshIndex < 0 || meshIndex >= this.selectedMaterialIndices.length) {
                return;
            }
            this.selectedMaterialIndices[meshIndex] = materialIndex;
            this.selectedMaterialIndices = [...this.selectedMaterialIndices];

            const selectedMesh = this.product.meshes?.[meshIndex];
            const selectedMaterial = selectedMesh?.materials?.[materialIndex];
            const selectedColor = selectedMaterial?.colorName;

            const ecommerceColorOption = this.ecommerceProduct.options.find(
                (option: Option) => option.type === 'Color'
            );

            if (ecommerceColorOption) {
                this.selectedOptions[ecommerceColorOption.name] = selectedColor;
            }
        },
        updateVariants(variantsData: VariantEcom[]) {
            const variants = this.ecommerceProduct.variants;
            variantsData.forEach((variantData: VariantEcom) => {
                const existingVariant = variants.find((variant) => variant.variantRef == variantData.variantRef);
                if (!existingVariant) {
                    console.error(
                        `Cannot update variant ${variantData.variantRef}. It does not exist in product variants`
                    );
                    return;
                }
                const variantIndex = variants.indexOf(existingVariant);
                const updatedVariant = EcommerceProductMapper.updateVariantFromEcomData(existingVariant, variantData);
                this.ecommerceProduct.variants[variantIndex] = updatedVariant;
            });
        }
    },
    getters: {
        colors: (state) => {
            const ecommerceColorOption = state.ecommerceProduct.options.find(
                (option: Option) => option.type === 'Color'
            );
            const colorList = state.product?.material?.colorList;
            if (ecommerceColorOption) {
                return colorList?.filter((color) => ecommerceColorOption.value.includes(color.name || ''));
            }
            return colorList;
        },
        selectedVariant: (state) => {
            const matchingVariant = state.ecommerceProduct.variants.find((variant) =>
                Object.entries(state.selectedOptions).every(([key, value]) => variant.options[key] === value)
            );

            return matchingVariant;
        },
        ecommerceColorOption: (state) => {
            return state.ecommerceProduct.options.find((option: Option) => option.type === 'Color');
        },
        ecommerceDisplayedOption: (state) => {
            return state.ecommerceProduct.options.find((option: Option) => option.type === 'DuringCustomization');
        },
        prices: (state) => {
            const matchingVariant = state.ecommerceProduct.variants.find((variant) =>
                Object.entries(state.selectedOptions).every(([key, value]) => variant.options[key] === value)
            );
            if (matchingVariant) {
                return matchingVariant.prices;
            }

            return state._prices;
        },
        getMetadata: (state) => {
            return () => {
                return {
                    productId: state.product?.id
                };
            };
        }
    }
});
